import React, { useState, useEffect } from 'react';
import { Send, Gift, CreditCard, Check, Eye } from 'lucide-react';
import { useAuth } from '../../App';
import { getProductsAdmin } from '../../api/products';
import { getTemplates } from '../../api/templates';
import { sendFree, sendCheckout } from '../../api/sends';
import AdminLayout from './AdminLayout';

const AdminSends = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendType, setSendType] = useState('free');
  const [formData, setFormData] = useState({
    email: '', name: '', product_ids: [], template_id: '', custom_price: ''
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, templatesData] = await Promise.all([
          getProductsAdmin(token),
          getTemplates(token)
        ]);
        setProducts(productsData);
        setTemplates(templatesData);
      } catch (err) {} finally { setLoading(false); }
    };
    if (token) fetchData();
  }, [token]);

  const toggleProduct = (id) => {
    setFormData(prev => ({
      ...prev,
      product_ids: prev.product_ids.includes(id) ? prev.product_ids.filter(p => p !== id) : [...prev.product_ids, id]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.product_ids.length === 0) {
      alert('Wybierz przynajmniej 1 produkt');
      return;
    }
    setSending(true);
    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        product_ids: formData.product_ids,
        template_id: formData.template_id,
        custom_price: formData.custom_price ? parseFloat(formData.custom_price) : null,
        is_free: sendType === 'free'
      };
      if (sendType === 'free') {
        await sendFree(token, payload);
        setSuccess('E-booki wysłane za darmo!');
      } else {
        const res = await sendCheckout(token, payload);
        if (res && res.email_sent === false) {
          alert('Link utworzony, ale e-mail NIE został wysłany:\n' + (res.email_error || 'nieznany błąd'));
        } else {
          setSuccess('Link do checkout wysłany!');
        }
      }
      setFormData({ email: '', name: '', product_ids: [], template_id: '', custom_price: '' });
    } catch (err) { alert('Błąd wysyłki'); } finally { setSending(false); }
  };

  const selectedTotal = formData.product_ids.reduce((sum, id) => {
    const p = products.find(pr => pr.id === id);
    return sum + (p ? (p.is_on_sale ? p.sale_price : p.regular_price) : 0);
  }, 0);

  return (
    <AdminLayout title="Wysyłki">
      <div className="max-w-3xl mx-auto">
        <div className="glass-card p-6 mb-6">
          <p className="text-[#9CA3AF] mb-4">Wyślij e-booki za darmo lub stwórz indywidualny checkout dla klienta.</p>
          
          {/* Send Type Toggle */}
          <div className="flex gap-4 mb-6">
            <button onClick={() => setSendType('free')} className={`flex-1 p-4 rounded-xl border transition-all ${sendType === 'free' ? 'border-[#10b981] bg-[#10b981]/10' : 'border-white/10'}`} data-testid="send-type-free">
              <Gift className={`mx-auto mb-2 ${sendType === 'free' ? 'text-[#10b981]' : 'text-[#6B7280]'}`} size={24} />
              <p className={`font-medium ${sendType === 'free' ? 'text-white' : 'text-[#9CA3AF]'}`}>🎁 GRATIS</p>
              <p className="text-[#6B7280] text-sm">Wyślij e-booki za darmo</p>
            </button>
            <button onClick={() => setSendType('checkout')} className={`flex-1 p-4 rounded-xl border transition-all ${sendType === 'checkout' ? 'border-[#10b981] bg-[#10b981]/10' : 'border-white/10'}`} data-testid="send-type-checkout">
              <CreditCard className={`mx-auto mb-2 ${sendType === 'checkout' ? 'text-[#10b981]' : 'text-[#6B7280]'}`} size={24} />
              <p className={`font-medium ${sendType === 'checkout' ? 'text-white' : 'text-[#9CA3AF]'}`}>💰 PŁATNE</p>
              <p className="text-[#6B7280] text-sm">Wyślij link do checkout</p>
            </button>
          </div>
        </div>

        {success && (
          <div className="bg-[#10b981]/20 text-[#10b981] p-4 rounded-xl mb-6 flex items-center gap-2">
            <Check size={20} /> {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Recipient */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Odbiorca</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Email *</label>
                <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full" placeholder="klient@email.pl" required data-testid="send-email" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Imię</label>
                <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full" placeholder="Jan" data-testid="send-name" />
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Wybierz produkty</h3>
            {loading ? <div className="spinner mx-auto"></div> : (
              <div className="space-y-2">
                {products.map(p => (
                  <label key={p.id} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${formData.product_ids.includes(p.id) ? 'border-[#10b981] bg-[#10b981]/10' : 'border-white/10 hover:border-white/20'}`} data-testid={`product-select-${p.id}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={formData.product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} className="checkbox-custom" />
                      <span className="text-white">{p.name}</span>
                    </div>
                    <span className="text-[#10b981] font-medium">{p.is_on_sale ? p.sale_price : p.regular_price} zł</span>
                  </label>
                ))}
              </div>
            )}
            {formData.product_ids.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/5 flex justify-between">
                <span className="text-[#9CA3AF]">Wybrano: {formData.product_ids.length} produktów</span>
                <span className="text-[#10b981] font-bold">Suma: {selectedTotal} zł</span>
              </div>
            )}
          </div>

          {/* Custom Price (for checkout only) */}
          {sendType === 'checkout' && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4">Cena (opcjonalnie)</h3>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Własna cena (zostaw puste = domyślna)</label>
                <input type="number" value={formData.custom_price} onChange={e => setFormData({...formData, custom_price: e.target.value})} className="w-full" placeholder={selectedTotal.toString()} min="0" step="0.01" data-testid="custom-price" />
              </div>
            </div>
          )}

          {/* Template */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Szablon email</h3>
            <select value={formData.template_id} onChange={e => setFormData({...formData, template_id: e.target.value})} className="w-full" data-testid="template-select">
              <option value="">Wybierz szablon</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          {/* Submit */}
          <button type="submit" disabled={sending} className="btn-primary w-full py-4" data-testid="send-submit">
            {sending ? <div className="spinner"></div> : (
              <>
                <Send size={18} />
                {sendType === 'free' ? 'Wyślij e-booki za darmo' : `Wyślij link do checkout (${formData.custom_price || selectedTotal} zł)`}
              </>
            )}
          </button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSends;